/**
 * UIX Theme Engine - Alap Témák
 *
 * Ezek a témák demonstrálják a rendszer extrém testreszabhatóságát.
 * A 5.4-es tesztben ezek cseréjével radikálisan megváltozik a termékkártya megjelenése.
 */

import { UixTheme } from "./theme.types";

// ============================================================
// DEFAULT (Semleges alaptéma – kb. a jelenlegi megjelenés)
// ============================================================
export const defaultTheme: UixTheme = {
    id: "default",
    name: "Default",
    colors: {
        surface: "#FFFFFF",
        background: "#F9FAFB",
        border: "#E5E7EB",
        textPrimary: "#111827",
        textSecondary: "#6B7280",
        textInverse: "#FFFFFF",
        brandPrimary: "#2563EB",
        sale: "#FEE2E2",
        saleText: "#991B1B",
        surfaceHover: "#F3F4F6",
    },
    typography: {
        fontFamily: "'Inter', sans-serif",
        baseFontSize: "16px",
        lineHeight: "1.5",
    },
    layout: {
        radiusCard: "8px",
        radiusButton: "6px",
        radiusBadge: "4px",
        shadowCard: "0 1px 3px rgba(0,0,0,0.1)",
        shadowButton: "none",
    },
    components: {
        button: {
            primaryBg: "#2563EB",
            primaryText: "#FFFFFF",
            primaryHover: "#1D4ED8",
            primaryBorder: "transparent",
        },
        card: {
            bg: "#FFFFFF",
            border: "#E5E7EB",
            hoverShadow: "0 4px 12px rgba(0,0,0,0.1)",
        },
        badge: {
            saleBg: "#FEE2E2",
            saleText: "#991B1B",
        },
        layouts: {
            productCard: {
                structure: "standard",
                density: "default",
                emphasis: "image",
            },
        },
    },
};

// ============================================================
// LUXURY (Minimalista prémium – serif fontok, arany, zéró rádiusz)
// ============================================================
export const luxuryTheme: UixTheme = {
    id: "luxury",
    name: "Luxury",
    colors: {
        surface: "#FDFAF6",
        background: "#F5F0E8",
        border: "#C8A96E",
        textPrimary: "#1A0F00",
        textSecondary: "#7A6A50",
        textInverse: "#FDFAF6",
        brandPrimary: "#8B6914",
        sale: "#1A0F00",
        saleText: "#C8A96E",
        surfaceHover: "#F0E8D8",
    },
    typography: {
        fontFamily: "'Playfair Display', serif",
        baseFontSize: "15px",
        lineHeight: "1.7",
    },
    layout: {
        radiusCard: "0px",
        radiusButton: "0px",
        radiusBadge: "0px",
        shadowCard: "none",
        shadowButton: "none",
    },
    components: {
        button: {
            primaryBg: "transparent",
            primaryText: "#1A0F00",
            primaryHover: "#F0E8D8",
            primaryBorder: "#C8A96E",
        },
        card: {
            bg: "#FDFAF6",
            border: "#C8A96E",
            hoverShadow: "none",
        },
        badge: {
            saleBg: "#1A0F00",
            saleText: "#C8A96E",
        },
        layouts: {
            productCard: {
                structure: "standard",
                density: "compact",
                emphasis: "content",
            },
        },
    },
    customCss: `
        h1, h2, h3, h4 { letter-spacing: 0.05em; text-transform: uppercase; }
        [data-uix-comp="add_to_cart_button"] { letter-spacing: 0.15em; font-size: 11px; }
    `,
};

// ============================================================
// CYBERPUNK (Neon, sötét, döntött sarkok, monospace)
// ============================================================
export const cyberpunkTheme: UixTheme = {
    id: "cyberpunk",
    name: "Cyberpunk",
    colors: {
        surface: "#0D0D1A",
        background: "#07070F",
        border: "#00FFC8",
        textPrimary: "#E0FFF8",
        textSecondary: "#00FFC8",
        textInverse: "#0D0D1A",
        brandPrimary: "#FF00AA",
        sale: "#FF00AA",
        saleText: "#FFFFFF",
        surfaceHover: "#1A1A2E",
    },
    typography: {
        fontFamily: "'Share Tech Mono', monospace",
        fontFamilyMono: "'Share Tech Mono', monospace",
        baseFontSize: "14px",
        lineHeight: "1.4",
    },
    layout: {
        radiusCard: "0px",
        radiusButton: "0px",
        radiusBadge: "0px",
        shadowCard: "0 0 15px rgba(0,255,200,0.2), inset 0 0 15px rgba(0,255,200,0.05)",
        shadowButton: "0 0 10px rgba(255,0,170,0.5)",
    },
    components: {
        button: {
            primaryBg: "#FF00AA",
            primaryText: "#FFFFFF",
            primaryHover: "#CC0088",
            primaryBorder: "#FF00AA",
        },
        card: {
            bg: "#0D0D1A",
            border: "#00FFC8",
            hoverShadow: "0 0 25px rgba(0,255,200,0.4)",
        },
        badge: {
            saleBg: "#FF00AA",
            saleText: "#FFFFFF",
        },
        layouts: {
            productCard: {
                structure: "overlay",
                density: "default",
                emphasis: "image",
            },
        },
    },
    customCss: `
        * { transition: all 0.15s ease; }
        [data-uix-slot="product.action.primary"]:hover { box-shadow: var(--uix-shadow-btn); transform: skewX(-5deg); }
    `,
};

// ============================================================
// ECO (Natúr, organikus – kerekítések, lágy pasztell)
// ============================================================
export const ecoTheme: UixTheme = {
    id: "eco",
    name: "Eco-Organic",
    colors: {
        surface: "#F8FFF4",
        background: "#EEF7E8",
        border: "#A8D5A2",
        textPrimary: "#1E3A1E",
        textSecondary: "#4A6741",
        textInverse: "#F8FFF4",
        brandPrimary: "#3A8C3A",
        sale: "#D4EDDA",
        saleText: "#155724",
        surfaceHover: "#E8F5E2",
    },
    typography: {
        fontFamily: "'Nunito', sans-serif",
        baseFontSize: "16px",
        lineHeight: "1.65",
    },
    layout: {
        radiusCard: "20px",
        radiusButton: "100px",
        radiusBadge: "100px",
        shadowCard: "0 4px 20px rgba(58,140,58,0.1)",
        shadowButton: "0 4px 12px rgba(58,140,58,0.25)",
    },
    components: {
        button: {
            primaryBg: "#3A8C3A",
            primaryText: "#FFFFFF",
            primaryHover: "#2D6E2D",
            primaryBorder: "transparent",
        },
        card: {
            bg: "#F8FFF4",
            border: "#A8D5A2",
            hoverShadow: "0 8px 24px rgba(58,140,58,0.15)",
        },
        badge: {
            saleBg: "#D4EDDA",
            saleText: "#155724",
        },
        layouts: {
            productCard: {
                structure: "standard",
                density: "default",
                emphasis: "image",
            },
        },
    },
};

export const themes: Record<string, UixTheme> = {
    default: defaultTheme,
    luxury: luxuryTheme,
    cyberpunk: cyberpunkTheme,
    eco: ecoTheme,
};
